'use client';

import React, { useEffect } from 'react';
import Script from 'next/script';
import { useCookieConsent } from '@/hooks/useCookieConsent';

const MEASUREMENT_ID = process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID;

// _ga cookies are written on the registrable domain, so expire them on every parent domain too
const clearGaCookies = () => {
  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));
  if (!names.length) {
    return;
  }

  const parts = window.location.hostname.split('.');
  const domains = parts.map((_, i) => parts.slice(i).join('.'));

  names.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`;
    });
  });
};

const GoogleAnalytics: React.FC = () => {
  const { consent, ready } = useCookieConsent();
  const allowed = ready && !!consent?.analytics;

  useEffect(() => {
    if (!ready || !MEASUREMENT_ID) {
      return;
    }

    // Blocks hits from a gtag already loaded earlier in this page session
    (window as any)[`ga-disable-${MEASUREMENT_ID}`] = !allowed;

    if (!allowed) {
      clearGaCookies();
    }
  }, [ready, allowed]);

  if (!allowed || !MEASUREMENT_ID) {
    return null;
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${MEASUREMENT_ID}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    </>
  );
};

export default GoogleAnalytics;
